import React from 'react';
import styled from 'styled-components';
import { Wand2, FileEdit } from 'lucide-react';
import { theme } from '../styles/theme';
import { ResponseMode } from '../types';

const Container = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const ModeToggle = styled.div`
  display: flex;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  overflow: hidden;
`;

const ModeButton = styled.button<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: none;
  background: ${({ $isActive, $isDisabled }) =>
    $isDisabled
      ? theme.colors.backgroundTertiary
      : $isActive
        ? theme.colors.primary[100]
        : theme.colors.surface
  };
  color: ${({ $isActive, $isDisabled }) =>
    $isDisabled
      ? theme.colors.textMuted
      : $isActive
        ? theme.colors.primary[700]
        : theme.colors.textSecondary
  };
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: ${({ $isDisabled }) => $isDisabled ? 'not-allowed' : 'pointer'};
  transition: all ${theme.transitions.base};
  position: relative;

  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.border};
  }

  &:hover:not(:disabled) {
    background: ${({ $isActive }) =>
      $isActive
        ? theme.colors.primary[200]
        : theme.colors.backgroundSecondary
    };
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px ${theme.colors.primary[500]}40;
  }

  ${({ $isActive }) => $isActive && `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: ${theme.colors.primary[500]};
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModeDescription = styled.div<{ mode: ResponseMode }>`
  margin-top: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};

  background: ${({ mode }) => {
    switch (mode) {
      case 'generate':
        return theme.colors.warning[100];
      case 'refine':
        return theme.colors.success[100];
      default:
        return 'transparent';
    }
  }};

  border: 1px solid ${({ mode }) => {
    switch (mode) {
      case 'generate':
        return theme.colors.warning[500];
      case 'refine':
        return theme.colors.success[500];
      default:
        return 'transparent';
    }
  }};

  color: ${({ mode }) => {
    switch (mode) {
      case 'generate':
        return theme.colors.warning[700];
      case 'refine':
        return theme.colors.success[700];
      default:
        return theme.colors.textSecondary;
    }
  }};
`;

interface ResponseModeSelectorProps {
  mode: ResponseMode;
  onChange: (mode: ResponseMode) => void;
  disabled?: boolean;
  className?: string;
}

const modeDescriptions: Record<ResponseMode, string> = {
  generate: "Generate draft responses for all questions using your knowledge base. Best for blank RFP/ITT templates where you need AI to create initial responses.",
  refine: "Refine existing responses to improve quality and polish them based on successful tender patterns. Best when your document already has responses."
};

const ResponseModeSelector: React.FC<ResponseModeSelectorProps> = ({
  mode,
  onChange,
  disabled = false,
  className
}) => {
  const handleModeChange = (newMode: ResponseMode) => {
    if (!disabled && newMode !== mode) {
      onChange(newMode);
    }
  };

  return (
    <Container className={className}>
      <ModeToggle>
        <ModeButton
          $isActive={mode === 'generate'}
          $isDisabled={disabled}
          onClick={() => handleModeChange('generate')}
          type="button"
          disabled={disabled}
          title="Generate new draft responses"
        >
          <Wand2 />
          Generate Draft
        </ModeButton>

        <ModeButton
          $isActive={mode === 'refine'}
          $isDisabled={disabled}
          onClick={() => handleModeChange('refine')}
          type="button"
          disabled={disabled}
          title="Refine existing responses"
        >
          <FileEdit />
          Refine Only
        </ModeButton>
      </ModeToggle>

      <ModeDescription mode={mode}>
        {modeDescriptions[mode]}
      </ModeDescription>
    </Container>
  );
};

export default ResponseModeSelector;
